/**
 * Certificate Form Drawer
 * Create/Edit certificate with file upload
 */

import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useTranslation, useForm, zodResolver, z } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@spcc/uikit'
import { Award, Save, Upload, X, FileText } from 'lucide-react'
import { certificatesActions } from '../../store/certificates.slice'
import { selectCertificatesDrawerOpen, selectCertificatesDrawerMode, selectSelectedCertificate, selectCertificatesSaving, selectCertificatesError } from '../../store/certificates.selectors'
import { mediaApi } from '../../infrastructure/api'
import { selectVariants } from '../../store/products.selectors'

// ============================================================================
// Form Schema
// ============================================================================

const certFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  isGeneral: z.boolean(),
  isActive: z.boolean(),
  variantDocumentId: z.string().optional(),
})

type CertFormData = z.infer<typeof certFormSchema>

// ============================================================================
// Helper Functions
// ============================================================================

function getDefaultValues(cert: ReturnType<typeof selectSelectedCertificate>): CertFormData {
  if (!cert) {
    return { title: '', description: '', isGeneral: true, isActive: true, variantDocumentId: undefined }
  }
  return {
    title: cert.title,
    description: cert.description ?? '',
    isGeneral: cert.isGeneral,
    isActive: cert.isActive,
    variantDocumentId: cert.variant?.documentId ?? undefined,
  }
}

// ============================================================================
// Component
// ============================================================================

export function CertificateFormDrawer() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isOpen = useSelector(selectCertificatesDrawerOpen)
  const mode = useSelector(selectCertificatesDrawerMode)
  const selected = useSelector(selectSelectedCertificate)
  const saving = useSelector(selectCertificatesSaving)
  const serverError = useSelector(selectCertificatesError)
  const variants = useSelector(selectVariants)

  const [file, setFile] = useState<File | null>(null)
  const [existingFileId, setExistingFileId] = useState<number | null>(null)
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isEditing = mode === 'edit'

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CertFormData>({
    resolver: zodResolver(certFormSchema),
    defaultValues: getDefaultValues(selected),
  })

  const isGeneral = watch('isGeneral')

  useEffect(() => {
    if (isOpen) {
      reset(getDefaultValues(selected))
      setFile(null)
      setUploadError(null)
      if (selected?.file) {
        setExistingFileId(selected.file.id)
        setExistingFileUrl(selected.file.url)
      } else {
        setExistingFileId(null)
        setExistingFileUrl(null)
      }
    }
  }, [isOpen, selected, reset])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    if (!picked) return
    if (picked.type !== 'application/pdf') {
      setUploadError(t('products.certificates.onlyPdfAccepted'))
      return
    }
    setFile(picked)
    setUploadError(null)
  }

  const handleRemoveFile = () => {
    setFile(null)
    setExistingFileId(null)
    setExistingFileUrl(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFormSubmit = async (data: CertFormData) => {
    setUploadError(null)
    let fileId: number | null | undefined = isEditing ? existingFileId : null

    if (file) {
      setUploading(true)
      try {
        const uploaded = await mediaApi.uploadImages([file])
        if (!uploaded.length || !uploaded[0].id) {
          setUploadError(t('products.certificates.uploadFailed'))
          setUploading(false)
          return
        }
        fileId = parseInt(uploaded[0].id, 10)
      } catch {
        setUploadError(t('products.certificates.uploadFailed'))
        setUploading(false)
        return
      }
      setUploading(false)
    }

    const payload = {
      title: data.title.trim(),
      description: data.description?.trim() || null,
      isGeneral: data.isGeneral,
      isActive: data.isActive,
      file: fileId ?? undefined,
      variant: data.isGeneral ? null : (data.variantDocumentId || null),
    }

    if (isEditing && selected) {
      dispatch(certificatesActions.updateCertificate({ documentId: selected.documentId, data: payload }))
    } else {
      dispatch(certificatesActions.createCertificate(payload))
    }
  }

  const handleClose = () => dispatch(certificatesActions.closeDrawer())

  const title = isEditing ? t('products.certificates.editCertificate') : t('products.certificates.addCertificate')
  const isBusy = saving || uploading

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={(open) => { if (!open) handleClose() }}
      headerContent={
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          <span className="font-semibold">{title}</span>
        </div>
      }
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-5 px-6 py-4">
        {/* Title */}
        <div className="space-y-1.5">
          <Label htmlFor="cert-title">{t('products.certificates.fieldTitle')} <span className="text-destructive">*</span></Label>
          <Input
            id="cert-title"
            placeholder={t('products.certificates.titlePlaceholder')}
            {...register('title')}
          />
          {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="cert-desc">{t('products.certificates.fieldDescription')}</Label>
          <Textarea
            id="cert-desc"
            placeholder={t('products.certificates.descriptionPlaceholder')}
            rows={3}
            {...register('description')}
          />
        </div>

        {/* Type toggles */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>{t('products.certificates.fieldGeneralCert')}</Label>
              <p className="text-xs text-muted-foreground">{t('products.certificates.generalCertHint')}</p>
            </div>
            <Switch
              checked={isGeneral}
              onCheckedChange={(v) => setValue('isGeneral', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>{t('products.certificates.fieldActive')}</Label>
              <p className="text-xs text-muted-foreground">{t('products.certificates.activeHint')}</p>
            </div>
            <Switch
              checked={watch('isActive')}
              onCheckedChange={(v) => setValue('isActive', v)}
            />
          </div>
        </div>

        {/* Variant link (only when not general) */}
        {!isGeneral && (
          <div className="space-y-1.5">
            <Label>{t('products.certificates.fieldLinkedVariant')}</Label>
            <Select
              value={watch('variantDocumentId') ?? ''}
              onValueChange={(v) => setValue('variantDocumentId', v || undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('products.certificates.selectVariantOptional')} />
              </SelectTrigger>
              <SelectContent>
                {variants.map((v) => (
                  <SelectItem key={v.documentId} value={v.documentId}>
                    {typeof v.name === 'string' ? v.name : v.name?.en ?? v.documentId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* PDF Upload */}
        <div className="space-y-2">
          <Label>{t('products.certificates.fieldPdf')} <span className="text-destructive">*</span></Label>

          {(existingFileUrl || file) ? (
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
              <FileText className="h-5 w-5 text-primary shrink-0" />
              <span className="flex-1 text-sm truncate text-foreground">
                {file ? file.name : existingFileUrl!.split('/').pop()}
              </span>
              <button type="button" onClick={handleRemoveFile} className="text-muted-foreground hover:text-destructive">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/20 px-4 py-6 text-sm text-muted-foreground hover:border-primary/40 hover:bg-muted/30 transition-colors"
            >
              <Upload className="h-4 w-4" />
              {t('products.certificates.uploadPdf')}
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={handleFileChange}
          />

          {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
        </div>

        {/* Server error */}
        {serverError && <p className="text-xs text-destructive">{serverError}</p>}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={isBusy}>
            {t('products.certificates.cancel')}
          </Button>
          <Button type="submit" className="flex-1 gap-2" disabled={isBusy}>
            {isBusy ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isEditing ? t('products.certificates.saveChanges') : t('products.certificates.createCertificate')}
          </Button>
        </div>
      </form>
    </BaseDrawer>
  )
}
